/**
 * Working out what the money just did, from one packet to the next.
 *
 * Chips move for three different reasons and only one of them is a move:
 *
 * - a **bet** is somebody putting chips on the felt, and arrives with an action
 * - a **sweep** is the end of a betting round, when everything on the felt goes
 *   into the pot. Nobody does it; it happens.
 * - a **payout** is the pot going to whoever won, which `usePayout` owns.
 *
 * Only the first has a `lastMove` behind it, so this reads the numbers rather
 * than the action. That also means it does not care WHICH game is being played
 * or what the move was called - a game that publishes a pot and per-seat
 * commitments gets its chips animated without naming a single move.
 *
 * ## The one that has to be got right
 *
 * A sweep and a settle look identical in the committed array: every seat drops
 * to zero. The difference is that a settle also pays the pot out and starts a
 * new hand, and animating chips INTO the pot at the moment it is being paid
 * out would send money both ways at once. The hand number is what separates
 * them, which is why it is read here rather than assumed.
 */

/** The money, as one packet reports it. */
export interface Money {
  /** What each seat has put in this betting round. */
  committed: readonly number[];
  /** Everything at stake, including what is still in front of the seats. */
  pot: number;
  /** Rises when a hand settles. Not a count of anything the money did. */
  hand: number;
}

export interface MoneyMove {
  seat: number;
  amount: number;
  /** `bet` travels from the chair to the felt; `sweep` from the felt to the pot. */
  kind: 'bet' | 'sweep';
}

function at(values: readonly number[], seat: number): number {
  const value = values[seat];
  return typeof value === 'number' && value > 0 ? value : 0;
}

/**
 * What to animate, given the money before and after.
 *
 * Returns nothing for a packet where the money did not move, which is most of
 * them - a chat message and a turn clock both arrive as packets.
 */
export function moneyMoves(before: Money, after: Money): MoneyMove[] {
  /*
   * A settled hand is not a sweep.
   *
   * Everything on the felt does go to zero, but the pot is being PAID rather
   * than filled, and `usePayout` is already sending it the other way. Without
   * this the last bet of every hand flew into the pot at the same moment the
   * pot flew out to the winner.
   */
  if (after.hand !== before.hand) return [];

  const seats = Math.max(before.committed.length, after.committed.length);

  const bets: MoneyMove[] = [];
  let wasOnFelt = 0;
  let isOnFelt = 0;
  for (let seat = 0; seat < seats; seat++) {
    const was = at(before.committed, seat);
    const is = at(after.committed, seat);
    wasOnFelt += was;
    isOnFelt += is;
    if (is > was) bets.push({ seat, amount: is - was, kind: 'bet' });
  }

  /*
   * The round closed: chips were on the felt, and now none are.
   *
   * Tested on the total rather than seat by seat, because one player folding
   * does not clear the felt - and a "sweep" fired per seat would send a folded
   * player's bet to the pot on its own while everybody else's stayed put.
   */
  if (wasOnFelt > 0 && isOnFelt === 0) {
    const sweep: MoneyMove[] = [];
    for (let seat = 0; seat < seats; seat++) {
      const was = at(before.committed, seat);
      if (was > 0) sweep.push({ seat, amount: was, kind: 'sweep' });
    }
    return sweep;
  }

  return bets;
}
